import {
  Badge, Button, Container, Group, Pagination,
  Stack, Switch, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { USER_ROLE_COLOURS, USER_ROLE_LABELS, type UserRole } from '../api/types';
import { useUsers } from '../hooks/useUsers';
import { BackToCompliance } from '../components/BackToCompliance';
import { RequireAccess } from '../components/RequireAccess';
import { SortableTh, nextSort, sortParam, type SortState } from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';
import {
  ColumnMultiSelectFilter, ColumnTextFilter, hasActiveFilters,
} from '../components/ColumnFilters';

const PAGE_SIZE = 50;
type UserSortKey = 'name' | 'email' | 'role' | 'login' | 'status';
const ROLE_FILTER_OPTIONS = (['admin', 'rm', 'compliance', 'read_only', 'staff'] as UserRole[])
  .map((r) => ({ value: r, label: USER_ROLE_LABELS[r] }));

/**
 * Users list — view-only.
 *
 * Pedro 2026-05-04: row-level destructive actions (the trashcan that used
 * to be in the Actions column) were removed. Disable / Reset password /
 * Sign-out-everywhere now live exclusively on the user detail page so
 * an admin has to deliberately navigate to a user before acting on them.
 * Same pattern as the Persons / Agreements / Accounts / Insurance
 * detail pages.
 *
 * Access (#4, §3h): the list endpoint (GET /users) is AdminUser-gated, so
 * the page is admins-only. RequireAccess shows a uniform friendly message
 * on direct-URL access by a non-admin instead of a broken/empty page; the
 * body (and its data hook) only mounts for admins.
 */
export function UsersListPage() {
  return (
    <RequireAccess roles={['admin']}>
      <UsersListBody />
    </RequireAccess>
  );
}

function UsersListBody() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // Pedro's smoke 2026-10-16 ("this table could use a filter in the header, for
  // consistency"): per-column filters (C-132) replace the search box and role dropdown.
  const [nameQ, setNameQ] = useState('');
  const [emailQ, setEmailQ] = useState('');
  const [roleF, setRoleF] = useState<string[]>([]);
  const filtersActive = hasActiveFilters({ nameQ, emailQ, roleF });
  function clearFilters() { setNameQ(''); setEmailQ(''); setRoleF([]); setPage(1); }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }
  // P41 phase 2 (2026-10-15): header sort, server-side across pages; null = by email.
  const [sort, setSortState] = useState<SortState<UserSortKey> | null>(null);
  function onSort(k: UserSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const { data, isLoading, error } = useUsers({
    role: roleF.length > 0 ? (roleF as UserRole[]) : undefined,
    name_q: nameQ || undefined,
    email_q: emailQ || undefined,
    include_deleted: includeDeleted,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance to="/admin" label="Back to Admin" />
        <Group justify="space-between">
          <Title order={2}>Users</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/users/new')}>
            New user
          </Button>
        </Group>

        <Switch
          label="Include deleted"
          checked={includeDeleted}
          onChange={(e) => { setIncludeDeleted(e.currentTarget.checked); setPage(1); }}
        />

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 phase 2 (2026-10-15): every header sorts (server-side). */}
              <SortableTh label="Name" col="name" sort={sort} onSort={onSort} filtered={!!nameQ} />
              <SortableTh label="Email" col="email" sort={sort} onSort={onSort} filtered={!!emailQ} />
              <SortableTh label="Role" col="role" sort={sort} onSort={onSort} w={160}
                filtered={roleF.length > 0} />
              <SortableTh label="Last login" col="login" sort={sort} onSort={onSort} w={180} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={120} />
            </Table.Tr>
            {/* Per-column filter row (C-132). Status is owned by "Include deleted" above. */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={nameQ} onChange={paged(setNameQ)}
                  placeholder="Name…" ariaLabel="Filter by name" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={emailQ} onChange={paged(setEmailQ)}
                  placeholder="Email…" ariaLabel="Filter by email" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={roleF} onChange={paged(setRoleF)}
                  options={ROLE_FILTER_OPTIONS} allLabel="All roles" ariaLabel="Filter by role" />
              </Table.Th>
              <Table.Th />
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />} onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>
            )}
            {data && data.items.length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No users found.</Text></Table.Td></Table.Tr>
            )}
            {data?.items.map((u) => (
              <Table.Tr
                key={u.id}
                style={{ cursor: 'pointer', opacity: u.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/users/${u.id}`)}
              >
                <Table.Td><Text fw={500}>{u.display_name}</Text></Table.Td>
                <Table.Td>{u.email}</Table.Td>
                <Table.Td>
                  {/* P41 phase 2: every role by its own name (was Admin / "Staff" for all others). */}
                  <Badge variant="light" color={USER_ROLE_COLOURS[u.role] ?? 'gray'}>
                    {USER_ROLE_LABELS[u.role] ?? u.role}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleString()
                    : <Text c="dimmed" size="sm">never</Text>}
                </Table.Td>
                <Table.Td>
                  <StatusExplained entity="user" status={u.deleted_at ? 'Deleted' : 'Active'}>
                  {u.deleted_at
                    ? <Badge color="gray" variant="light">Deleted</Badge>
                    : <Badge color="green" variant="light">Active</Badge>}
                  </StatusExplained>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
